import { Injectable, effect, DestroyRef, inject } from '@angular/core';
import { BehaviorSubject, Subject, debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuItem } from 'primeng/api';
import { JWTService } from 'app/core/services/jwt.service';
import { AuthService } from 'app/core/services/auth.service';
import { FeatureFlagsService } from 'app/core/services/feature-flags.service';
import { UserRole } from 'app/core/enums/user-role.enum';
import { FeatureFlag } from 'app/core/enums/feature-flag.enum';

interface MenuItemComFlag extends MenuItem {
  roles?: UserRole[];
  featureFlag?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private readonly destroyRef = inject(DestroyRef);

  private readonly menuItems$ = new BehaviorSubject<MenuItem[]>([]);
  public menuItems = this.menuItems$.asObservable();

  private readonly buildMenuTrigger$ = new Subject<void>();

  private readonly allMenuItems: MenuItemComFlag[] = [
    {
      label: 'Leads',
      icon: 'pi pi-users',
      route: '/leads',
      roles: [
        UserRole.SALES_REP,
        UserRole.SALES_OPS,
        UserRole.FIELD_SALES,
        UserRole.ADMIN,
      ],
      featureFlag: 'leads',
    },
    {
      label: 'Empresas',
      icon: 'pi pi-building',
      route: '/empresas',
      roles: [
        UserRole.SALES_REP,
        UserRole.SALES_OPS,
        UserRole.FIELD_SALES,
        UserRole.ADMIN,
      ],
      featureFlag: 'empresas',
    },
    {
      label: 'Propostas',
      icon: 'pi pi-file',
      route: '/propostas',
      roles: [UserRole.SALES_REP, UserRole.ADMIN],
      featureFlag: 'proposta_sales_rep',
    },
    {
      label: 'Avaliação',
      icon: 'pi pi-desktop',
      route: '/propostas/avaliacao',
      roles: [UserRole.SALES_OPS, UserRole.ADMIN],
      featureFlag: 'proposta_sales_ops',
    },
    {
      label: 'Validador de template',
      icon: 'pi pi-file-edit',
      route: '/propostas/template',
      roles: [UserRole.ADMIN, UserRole.BACKOFFICE],
    },
    {
      label: 'Contratos',
      icon: 'pi pi-file-import',
      route: '/contratos/avaliacao',
      roles: [UserRole.SALES_OPS, UserRole.ADMIN],
      featureFlag: 'tratativas_envio_analise',
    },
    {
      label: 'Dashboard',
      icon: 'pi pi-chart-line',
      route: '/indicadores',
      roles: [
        UserRole.SALES_REP,
        UserRole.FIELD_SALES,
        UserRole.SALES_OPS,
        UserRole.ADMIN,
      ],
      featureFlag: FeatureFlag.INDICADORES_DASHBOARD,
    },
    {
      label: 'Beneficiários',
      icon: 'pi pi-id-card',
      route: '/beneficiarios/importar',
      roles: [UserRole.SALES_FARM, UserRole.ADMIN],
      featureFlag: FeatureFlag.NEGOCIACAO_MULTIPRODUTO,
    },
  ];

  constructor(
    private readonly jwtService: JWTService,
    private readonly authService: AuthService,
    private readonly featureFlagsService: FeatureFlagsService,
  ) {
    this.buildMenuTrigger$
      .pipe(debounceTime(50), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.executarBuildMenu());

    this.authService.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        if (user) {
          this.buildMenuTrigger$.next();
        } else {
          this.menuItems$.next([]);
        }
      });

    effect(() => {
      const flags = this.featureFlagsService.obterTodasFlags()();
      if (flags && Object.keys(flags).length > 0) {
        this.buildMenuTrigger$.next();
      }
    });

    this.buildMenuTrigger$.next();
  }

  private executarBuildMenu(): void {
    const userRole = this.jwtService.getRole() as UserRole;

    if (!userRole) {
      this.menuItems$.next([]);
      return;
    }

    const filteredItems = this.allMenuItems
      .filter(item => !item.roles || item.roles.includes(userRole))
      .filter(item => {
        if (item.featureFlag) {
          return this.featureFlagsService.estaHabilitado(item.featureFlag);
        }
        return true;
      })
      .map(item => ({
        label: item.label,
        icon: item.icon,
        ['route']: item['route'],
      }));

    const orderedItems = this.ordenarMenuPorRole(filteredItems, userRole);
    this.menuItems$.next(orderedItems);
  }

  private ordenarMenuPorRole(items: MenuItem[], role: UserRole): MenuItem[] {
    const ordemPorRole: Record<UserRole, string[]> = {
      [UserRole.SALES_REP]: [
        '/leads',
        '/empresas',
        '/propostas',
        '/indicadores',
      ],
      [UserRole.FIELD_SALES]: ['/leads', '/empresas', '/indicadores'],
      [UserRole.SALES_OPS]: [
        '/propostas/avaliacao',
        '/contratos/avaliacao',
        '/leads',
        '/empresas',
        '/indicadores',
      ],
      [UserRole.BACKOFFICE]: ['/propostas/template'],
      [UserRole.SALES_FARM]: ['/beneficiarios/importar'],
      [UserRole.ADMIN]: [
        '/propostas',
        '/propostas/avaliacao',
        '/contratos/avaliacao',
        '/leads',
        '/empresas',
        '/beneficiarios/importar',
        '/indicadores',
      ],
    };

    const ordem = ordemPorRole[role] || [];

    return items.sort((a, b) => {
      const indexA = ordem.indexOf(a['route'] as string);
      const indexB = ordem.indexOf(b['route'] as string);

      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  }

  public refreshMenu(): void {
    this.buildMenuTrigger$.next();
  }
}
